import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, Sparkles, ArrowLeft, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import type { QuizQuestion } from "@/lib/quiz-generator";

type PolicyQuiz = Database["public"]["Tables"]["policy_quizzes"]["Row"];
type QuizAttempt = Database["public"]["Tables"]["quiz_attempts"]["Row"];

type ViewState = "play" | "result";

export default function QuizTake() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState<PolicyQuiz | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [view, setView] = useState<ViewState>("play");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.company_id && id) {
      void loadQuiz();
    }
  }, [profile?.company_id, id]);

  const loadQuiz = async () => {
    if (!profile?.company_id || !id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: qErr } = await supabase
        .from("policy_quizzes")
        .select("*")
        .eq("company_id", profile.company_id)
        .eq("id", id)
        .maybeSingle();

      if (qErr) throw qErr;
      if (!data) {
        setError("Quiz not found or no longer available.");
        setQuiz(null);
        setQuestions([]);
        return;
      }

      const q = data as PolicyQuiz;
      const parsedQuestions: QuizQuestion[] = Array.isArray((q as any).questions)
        ? (q as any).questions
        : [];

      if (!parsedQuestions.length) {
        throw new Error("This quiz has no questions configured.");
      }

      setQuiz(q);
      setQuestions(parsedQuestions);
      setCurrentIndex(0);
      setSelectedIndex(null);
      setRevealed(false);
      setScore(0);
      setCorrectCount(0);
      setView("play");
      setStartTime(Date.now());
    } catch (err: any) {
      console.error("Error loading quiz:", err);
      setError(
        err?.message ||
          "We couldn't load this quiz. It may have been removed or you may not have access."
      );
      setQuiz(null);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = useMemo(
    () => (questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0),
    [currentIndex, questions.length]
  );

  const handleSelect = (index: number) => {
    if (revealed) return;
    setSelectedIndex(index);
  };

  const handleReveal = () => {
    if (selectedIndex === null || !currentQuestion) return;
    const isCorrect = selectedIndex === currentQuestion.correctIndex;
    setRevealed(true);
    if (isCorrect) {
      setCorrectCount((c) => c + 1);
      setScore((s) => s + (currentQuestion.points || 10));
    }
  };

  const handleNext = async () => {
    if (!revealed) {
      handleReveal();
      return;
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setSelectedIndex(null);
      setRevealed(false);
      return;
    }

    await finishQuiz();
  };

  const finishQuiz = async () => {
    if (!quiz || !profile?.id || !profile.company_id) {
      setView("result");
      return;
    }

    const maxScore = questions.reduce((sum, q) => sum + (q.points || 10), 0);
    const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    const timeTakenSeconds = startTime ? Math.round((Date.now() - startTime) / 1000) : null;

    setSaving(true);
    try {
      const payload = {
        quiz_id: quiz.id,
        user_id: profile.id,
        company_id: profile.company_id,
        score: percentage,
        correct_answers: correctCount,
        total_questions: questions.length,
        answers: {}, // we only store aggregate for now; can extend later
        time_taken_seconds: timeTakenSeconds,
      };

      const { error: insertError } = await supabase.from("quiz_attempts").insert(payload);
      if (insertError) {
        console.error("Error saving quiz attempt:", insertError);
      }
    } finally {
      setSaving(false);
      setView("result");
    }
  };

  const resetQuiz = () => {
    setCurrentIndex(0);
    setSelectedIndex(null);
    setRevealed(false);
    setScore(0);
    setCorrectCount(0);
    setView("play");
    setStartTime(Date.now());
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-8 w-24" />
        </div>
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !quiz || questions.length === 0) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="px-0"
          onClick={() => navigate("/quizzes")}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to quizzes
        </Button>
        <Alert variant="destructive">
          <AlertDescription>{error || "Quiz is not available."}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const maxScore = questions.reduce((sum, q) => sum + (q.points || 10), 0);
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          className="px-0"
          onClick={() => navigate("/quizzes")}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to quizzes
        </Button>
        <div className="flex items-center gap-3">
          <Badge variant="outline">
            Question {currentIndex + 1} of {questions.length}
          </Badge>
          <div className="w-40">
            <Progress value={progress} className="h-1.5" />
          </div>
        </div>
      </div>

      <Card className="shadow-card overflow-hidden">
        <CardHeader className="border-b bg-muted/40">
          <CardTitle className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="truncate">{quiz.title}</span>
            </span>
            <Badge variant="outline" className="hidden sm:inline-flex">
              Quick policy check
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AnimatePresence mode="wait">
            {view === "play" ? (
              <motion.div
                key={`q-${currentIndex}`}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.25 }}
                className="p-6 space-y-5"
              >
                <div className="space-y-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Flashcard
                  </div>
                  <h2 className="text-lg font-semibold leading-snug">
                    {currentQuestion?.question}
                  </h2>
                </div>

                <div className="space-y-3">
                  {currentQuestion?.options.map((option, index) => {
                    const isSelected = selectedIndex === index;
                    const isCorrect = currentQuestion.correctIndex === index;
                    const isWrongChoice = revealed && isSelected && !isCorrect;

                    let variant: "outline" | "default" | "secondary" = "outline";
                    if (revealed && isCorrect) {
                      variant = "default";
                    } else if (isSelected) {
                      variant = "secondary";
                    }

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(index)}
                        className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all flex items-center justify-between gap-3 ${
                          isCorrect && revealed
                            ? "border-emerald-500 bg-emerald-500/10"
                            : isWrongChoice
                            ? "border-destructive bg-destructive/5"
                            : isSelected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/40 hover:bg-muted/60"
                        }`}
                      >
                        <span className="flex-1">{option}</span>
                        {revealed && (
                          <span className="shrink-0">
                            {isCorrect ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            ) : isWrongChoice ? (
                              <XCircle className="w-4 h-4 text-destructive" />
                            ) : null}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="space-y-3">
                  {revealed && currentQuestion?.explanation && (
                    <Alert className="border-emerald-500/40 bg-emerald-500/5 text-sm">
                      <AlertDescription>{currentQuestion.explanation}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex items-center justify-between gap-3 pt-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>Answer to reveal the explanation.</span>
                    </div>
                    <div className="flex gap-2">
                      {!revealed ? (
                        <Button
                          size="sm"
                          disabled={selectedIndex === null}
                          onClick={handleReveal}
                        >
                          Check Answer
                        </Button>
                      ) : (
                        <Button size="sm" onClick={handleNext}>
                          {currentIndex === questions.length - 1
                            ? "See Results"
                            : "Next Question"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="p-6 space-y-5 text-center"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="inline-flex items-center justify-center rounded-full bg-primary/10 p-3">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold">
                    {percentage >= 80
                      ? "Great work!"
                      : percentage >= 60
                      ? "Nice effort!"
                      : "Good start!"}
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-md">
                    {percentage >= 80
                      ? "You have a strong grasp of this policy. Keep it up!"
                      : percentage >= 60
                      ? "You&apos;re on your way. A quick review of the policy will make you even stronger."
                      : "Consider revisiting the policy and trying again to build more confidence."}
                  </p>
                </div>

                <div className="space-y-3 max-w-md mx-auto">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Score</span>
                    <span className="font-medium text-foreground">{percentage}%</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {correctCount}/{questions.length} questions correct
                    </span>
                    <span>{maxScore} pts max</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate("/quizzes");
                    }}
                  >
                    Back to quizzes
                  </Button>
                  <Button onClick={resetQuiz} disabled={saving}>
                    Retake quiz
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </div>
  );
}

